import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { useProjects } from "@/contexts/ProjectsContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Clock, Square } from "lucide-react";
import { format } from "date-fns";
import { useTimer } from "@/hooks/useTimer";

const TIMER_ENABLED = import.meta.env.VITE_ENABLE_TIMER === "true";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];

const schema = z.object({
  title: z.string().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  description: z
    .string()
    .max(5000, "Description must be less than 5000 characters")
    .optional()
    .or(z.literal("")),
  status: z.enum(["todo", "in_progress", "done", "review"]).default("todo"),
  priority: z.enum(["low", "medium", "high"]).optional().nullable(),
  due_date: z.string().optional().nullable(),
  tags: z.string().optional(),
  project_id: z.string().optional(),
  assigned_to: z.string().optional().nullable(),
});

type TaskFormValues = {
  title: string;
  description?: string;
  status: "todo" | "in_progress" | "done" | "review";
  priority?: "low" | "medium" | "high" | null;
  due_date?: string | null;
  tags?: string;
  project_id?: string;
  assigned_to?: string | null;
};

type ProjectMemberRow = Database["public"]["Views"]["project_members_with_profiles"]["Row"];
type ProjectData = Database["public"]["Functions"]["get_user_projects"]["Returns"][number];

// Partial update type for task updates
type TaskPartialUpdate = Partial<TaskRow> & { id: string };

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (task?: TaskWithDetailsRow | TaskPartialUpdate) => void;
  onDelete?: (taskId: string) => void;
  projectId?: string;
  task?: TaskWithDetailsRow | null;
  mode?: "create" | "edit";
  initialStatus?: "todo" | "in_progress" | "review" | "done";
  projects?: ProjectData[];
}

export function TaskDialog({
  open,
  onOpenChange,
  onSuccess,
  onDelete,
  projectId,
  task,
  mode = "create",
  initialStatus,
  projects: projectsProp,
}: TaskDialogProps) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);
  const [projectMembers, setProjectMembers] = useState<ProjectMemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const { activeTimer, elapsedTime, startTimer, stopTimer, loading: timerLoading } = useTimer();
  const { projects: projectsFromContext } = useProjects();
  const { user } = useAuth();

  // Get current user ID from auth context
  const currentUserId = user?.id ?? null;

  // Use projects from prop if provided, otherwise use context
  const projects = projectsProp || projectsFromContext;

  // Determine if project selection should be shown
  const showProjectSelection = mode === "create" && !projectId && projects && projects.length > 0;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      status: "todo",
      priority: null,
      due_date: null,
      tags: "",
      project_id: projectId || "",
      assigned_to: null,
    },
    mode: "onTouched",
  });

  const watchedProjectId = form.watch("project_id");

  // Permissions
  const currentSelectedProjectIdForPerms = watchedProjectId || projectId || task?.project_id;
  const selectedProjectForPerms = projects?.find(
    (p: ProjectData) => p.project_id === currentSelectedProjectIdForPerms,
  );
  const contextRole = (selectedProjectForPerms?.members as any[])?.find(
    (m: any) => m.user_id === currentUserId,
  )?.role;

  const currentUserMember = projectMembers.find((m) => m.user_id === currentUserId);
  const currentUserRole = currentUserMember?.role || contextRole;

  const isOwnerOrEditor = currentUserRole === "owner" || currentUserRole === "editor";
  const isViewer = currentUserRole === "viewer";
  const isAssignedToMe = currentUserId && task?.assigned_to === currentUserId;

  const canEditContent = isOwnerOrEditor;
  const canMarkReview = isViewer && isAssignedToMe;
  const canCreate = isOwnerOrEditor;

  const isContentDisabled = mode === "edit" && !canEditContent;

  // Fetch project members when project is selected
  useEffect(() => {
    const fetchProjectMembers = async () => {
      const currentProjectId = watchedProjectId || projectId || task?.project_id;

      if (!currentProjectId) {
        setProjectMembers([]);
        return;
      }

      setLoadingMembers(true);
      try {
        // Fetch project members using the view (includes owner and all members with profiles)
        // Note: View is not in generated types yet, so we use type assertion
        const { data: membersData, error } = await supabase
          .from("project_members_with_profiles")
          .select("*")
          .eq("project_id", currentProjectId)
          .order("role", { ascending: false }) // Owner first, then by role
          .order("added_at", { ascending: true });

        if (error) {
          console.error("Error fetching project members:", error);
          setProjectMembers([]);
        } else {
          setProjectMembers(membersData || []);
        }
      } catch (err) {
        console.error("Error fetching project members:", err);
        setProjectMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchProjectMembers();
  }, [watchedProjectId, projectId, task?.project_id]);

  useEffect(() => {
    if (task && mode === "edit") {
      form.reset({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "todo",
        priority: task.priority || null,
        due_date: task.due_date || null,
        tags: task.tags?.join(", ") || "",
        project_id: task.project_id || "",
        assigned_to: task.assigned_to || null,
      });
      setShowOptional(true);
    } else if (open && mode === "create") {
      // Set default due date to today
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      form.reset({
        title: "",
        description: "",
        status: initialStatus || "todo",
        priority: null,
        due_date: todayStr,
        tags: "",
        project_id: projectId || "",
        assigned_to: null,
      });
      form.reset({
        title: "",
        description: "",
        status: initialStatus || "todo",
        priority: null,
        due_date: todayStr,
        tags: "",
        project_id: projectId || "",
        assigned_to: null,
      });
      setShowOptional(true); // Show optional fields since due_date is set
    }
  }, [task, mode, open, initialStatus, projectId, form]);

  // If creating and not allowed, show message or close (handled in render)

  async function handleStatusChange(newStatus: "todo" | "in_progress" | "done" | "review") {
    if (!task) return;
    setError(null);
    setLoading(true);

    try {
      if (!user) {
        throw new Error("You must be logged in");
      }

      const { data: updatedData, error: updateError } = await supabase
        .from("tasks")
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", task.id!)
        .select()
        .single();

      if (updateError) throw updateError;

      // Get assigned user info (should be same as before since we're only changing status)
      const currentAssignedTo = projectMembers.find((m) => m.user_id === task.assigned_to);

      onOpenChange(false);
      // Return only the updated fields as a partial update
      const partialUpdate: TaskPartialUpdate &
        Partial<
          Pick<
            TaskWithDetailsRow,
            "assigned_username" | "assigned_full_name" | "assigned_avatar_url"
          >
        > = {
        id: updatedData.id,
        status: updatedData.status,
        updated_at: updatedData.updated_at,
      };

      // Preserve assigned user info if task was assigned
      if (currentAssignedTo) {
        partialUpdate.assigned_username = currentAssignedTo.username;
        partialUpdate.assigned_full_name = currentAssignedTo.full_name;
        partialUpdate.assigned_avatar_url = currentAssignedTo.avatar_url;
      }

      onSuccess?.(partialUpdate);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update task status";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit(values: TaskFormValues) {
    setError(null);
    setLoading(true);

    try {
      if (!user) {
        throw new Error("You must be logged in");
      }

      const tagsArray = values.tags
        ? values.tags
            .split(",")
            .map((t: string) => t.trim())
            .filter(Boolean)
        : null;

      if (mode === "create") {
        const selectedProjectId = values.project_id || projectId;
        if (!selectedProjectId) {
          throw new Error("Please select a project");
        }

        const selectedProject = projects?.find(
          (p: ProjectData) => p.project_id === selectedProjectId,
        );
        if (!selectedProject) {
          throw new Error("Project not found");
        }

        const selectedAssignedTo = projectMembers.find((m) => m.user_id === values.assigned_to);
        const selectedCreator = projectMembers.find((m) => m.user_id === user.id);

        const { data: insertedData, error: insertError } = await supabase
          .from("tasks")
          .insert({
            title: values.title,
            description: values.description || null,
            status: values.status,
            priority: values.priority || null,
            due_date: values.due_date || null,
            tags: tagsArray,
            project_id: selectedProjectId,
            created_by: user.id,
            assigned_to: values.assigned_to || null,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        form.reset();
        onOpenChange(false);
        // Return full TaskWithDetailsRow built from available data
        onSuccess?.({
          assigned_avatar_url: selectedAssignedTo?.avatar_url || null,
          assigned_full_name: selectedAssignedTo?.full_name || null,
          assigned_to: selectedAssignedTo?.user_id || null,
          assigned_username: selectedAssignedTo?.username || null,
          completed_at: null,
          completed_avatar_url: null,
          completed_by: null,
          completed_full_name: null,
          completed_username: null,
          created_at: insertedData.created_at || null,
          created_by: user.id,
          creator_avatar_url: selectedCreator?.avatar_url || null,
          creator_full_name: selectedCreator?.full_name || null,
          creator_username: selectedCreator?.username || null,
          description: insertedData.description || null,
          due_date: insertedData.due_date || null,
          id: insertedData.id || null,
          priority: insertedData.priority || null,
          project_id: insertedData.project_id || null,
          project_name: selectedProject.project_title || null,
          reminder_date: insertedData.reminder_date || null,
          status: insertedData.status || null,
          tags: insertedData.tags || null,
          title: insertedData.title || null,
          updated_at: insertedData.updated_at || null,
          updated_by: null,
          updater_avatar_url: null,
          updater_full_name: null,
          updater_username: null,
        });
      } else if (task) {
        const { data: updatedData, error: updateError } = await supabase
          .from("tasks")
          .update({
            title: values.title,
            description: values.description || null,
            status: values.status,
            priority: values.priority || null,
            due_date: values.due_date || null,
            tags: tagsArray,
            assigned_to: values.assigned_to || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", task.id!)
          .select()
          .single();

        if (updateError) throw updateError;

        // Get assigned user info if assignment changed
        const newAssignedTo = projectMembers.find((m) => m.user_id === updatedData.assigned_to);

        form.reset();
        onOpenChange(false);
        // Return only the updated fields as a partial update
        // Include assigned user info if assignment changed
        const partialUpdate: TaskPartialUpdate &
          Partial<
            Pick<
              TaskWithDetailsRow,
              "assigned_username" | "assigned_full_name" | "assigned_avatar_url"
            >
          > = {
          id: updatedData.id,
          title: updatedData.title,
          description: updatedData.description,
          status: updatedData.status,
          priority: updatedData.priority,
          due_date: updatedData.due_date,
          tags: updatedData.tags,
          assigned_to: updatedData.assigned_to,
          updated_at: updatedData.updated_at,
        };

        // Add assigned user info if available
        if (newAssignedTo) {
          partialUpdate.assigned_username = newAssignedTo.username;
          partialUpdate.assigned_full_name = newAssignedTo.full_name;
          partialUpdate.assigned_avatar_url = newAssignedTo.avatar_url;
        } else if (updatedData.assigned_to === null) {
          // If unassigned, clear the assigned user fields
          partialUpdate.assigned_username = null;
          partialUpdate.assigned_full_name = null;
          partialUpdate.assigned_avatar_url = null;
        }

        onSuccess?.(partialUpdate);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save task";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  const isCompact = mode === "edit";
  const currentSelectedProjectId = watchedProjectId || projectId || task?.project_id;

  if (
    mode === "create" &&
    currentSelectedProjectId &&
    !canCreate &&
    !loadingMembers &&
    currentUserId
  ) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="p-4 text-center space-y-4">
            <div className="text-destructive font-medium">
              You do not have permission to create tasks.
            </div>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isTrackingThisTask = task?.id && activeTimer?.taskId === task.id;

  // Format seconds to HH:MM:SS
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={isCompact ? "max-w-lg max-h-[85vh] overflow-y-auto" : "max-w-lg"}
        showCloseButton={false}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={() => {
          // Allow Escape key to close
          onOpenChange(false);
        }}
      >
        <DialogHeader className={isCompact ? "pb-2" : ""}>
          <div className="flex items-center justify-between">
            <DialogTitle className={isCompact ? "text-lg" : "sr-only"}>
              {mode === "create" ? "Create new task" : "Edit task"}
            </DialogTitle>
            {mode === "edit" && task && (
              <div className="flex items-center gap-2">
                {/* Time Tracking Button */}
                {TIMER_ENABLED && task.id && (
                  <Button
                    type="button"
                    variant={isTrackingThisTask ? "destructive" : "default"}
                    size="sm"
                    onClick={async () => {
                      if (isTrackingThisTask) {
                        await stopTimer();
                      } else {
                        await startTimer(task.id!);
                      }
                    }}
                    disabled={timerLoading}
                    className="flex items-center gap-2"
                  >
                    {isTrackingThisTask ? (
                      <>
                        <Square className="h-4 w-4" />
                        <span className="font-mono text-xs">{formatTime(elapsedTime)}</span>
                        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4" />
                        <span>Start Tracking</span>
                      </>
                    )}
                  </Button>
                )}
                {/* Viewer Actions */}
                {canMarkReview && task.status !== "review" && task.status !== "done" && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleStatusChange("review")}
                    disabled={loading || deleting}
                  >
                    Submit for Review
                  </Button>
                )}

                {/* Owner/Editor Actions */}
                {canEditContent && (
                  <>
                    {task.status === "todo" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange("in_progress")}
                        disabled={loading || deleting}
                      >
                        Start Task
                      </Button>
                    )}

                    {task.status === "review" && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleStatusChange("in_progress")}
                          disabled={loading || deleting}
                        >
                          Reject
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleStatusChange("done")}
                          disabled={loading || deleting}
                        >
                          Approve
                        </Button>
                      </>
                    )}

                    {task.status === "done" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange("todo")}
                        disabled={loading || deleting}
                      >
                        Re-open
                      </Button>
                    )}

                    {(task.status === "todo" || task.status === "in_progress") && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleStatusChange("done")}
                        disabled={loading || deleting}
                      >
                        Mark as Completed
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        <Form {...form}>
          <form
            id="task-dialog-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className={isCompact ? "space-y-3" : "space-y-4"}
          >
            {/* Task Name */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Task name"
                      className={isCompact ? "text-sm font-medium h-9" : "text-base font-medium"}
                      disabled={isContentDisabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Description (optional)"
                      className={
                        isCompact ? "resize-none min-h-[60px] text-sm" : "resize-none min-h-[80px]"
                      }
                      rows={isCompact ? 2 : 3}
                      disabled={isContentDisabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Project Selection (only when creating and projectId is not provided) */}
            {showProjectSelection && (
              <FormField
                control={form.control}
                name="project_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Project</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""}
                      disabled={isContentDisabled}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a project" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {projects?.map((project: ProjectData) => (
                          <SelectItem key={project.project_id} value={project.project_id}>
                            {project.project_title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Assigned to - Always visible when project is selected */}
            {(form.watch("project_id") || projectId || task?.project_id) && (
              <FormField
                control={form.control}
                name="assigned_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={isCompact ? "text-xs" : "text-sm"}>Assigned to</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                      value={field.value || "none"}
                      disabled={loadingMembers || isContentDisabled}
                    >
                      <FormControl>
                        <SelectTrigger className={isCompact ? "h-8 text-xs" : ""}>
                          <SelectValue
                            placeholder={loadingMembers ? "Loading..." : "Select assignee"}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {projectMembers.map((member) => (
                          <SelectItem key={member.user_id!} value={member.user_id!}>
                            {member.full_name || member.username || "Unknown User"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Optional Fields - Collapsible */}
            {showOptional || mode === "edit" ? (
              <div className={isCompact ? "space-y-2 pt-2 border-t" : "space-y-3 pt-2 border-t"}>
                <div
                  className={
                    isCompact
                      ? "grid grid-cols-1 md:grid-cols-2 gap-2"
                      : "grid grid-cols-1 md:grid-cols-2 gap-3"
                  }
                >
                  {/* Due Date */}
                  <FormField
                    control={form.control}
                    name="due_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={isCompact ? "text-xs" : "text-sm"}>
                          Due date
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                className={
                                  isCompact
                                    ? "w-full justify-start text-left font-normal h-8 text-xs"
                                    : "w-full justify-start text-left font-normal"
                                }
                                disabled={isContentDisabled}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(new Date(field.value + "T00:00:00"), "PPP")
                                ) : (
                                  <span className="text-muted-foreground">Pick a date</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={
                                field.value ? new Date(field.value + "T00:00:00") : undefined
                              }
                              onSelect={(date) => {
                                if (date) {
                                  // Format date as YYYY-MM-DD in local timezone
                                  const year = date.getFullYear();
                                  const month = String(date.getMonth() + 1).padStart(2, "0");
                                  const day = String(date.getDate()).padStart(2, "0");
                                  field.onChange(`${year}-${month}-${day}`);
                                } else {
                                  field.onChange(null);
                                }
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Priority */}
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={isCompact ? "text-xs" : "text-sm"}>
                          Priority
                        </FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                          value={field.value || "none"}
                          disabled={isContentDisabled}
                        >
                          <FormControl>
                            <SelectTrigger className={isCompact ? "h-8 text-xs" : ""}>
                              <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Tags */}
                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={isCompact ? "text-xs" : "text-sm"}>Tags</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="tag1, tag2, tag3"
                          value={field.value || ""}
                          className={isCompact ? "h-8 text-xs" : ""}
                          disabled={isContentDisabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowOptional(true)}
                className="text-xs text-muted-foreground"
              >
                + Add due date, priority, tags
              </Button>
            )}

            {error && (
              <div
                className={
                  isCompact
                    ? "rounded-md border border-destructive/30 bg-destructive/5 p-1.5 text-xs text-destructive"
                    : "rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
                }
              >
                {error}
              </div>
            )}

            <DialogFooter className={isCompact ? "pt-2" : ""}>
              {mode === "edit" && onDelete && task && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={async () => {
                    if (!confirm("Are you sure you want to delete this task?")) return;
                    setDeleting(true);
                    setError(null);
                    try {
                      onDelete(task.id!);
                      onOpenChange(false);
                      onSuccess?.();
                    } catch (err) {
                      const message = err instanceof Error ? err.message : "Failed to delete task";
                      setError(message);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={loading || deleting}
                  className="mr-auto"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading || deleting}
              >
                Cancel
              </Button>
              {mode === "create" && (
                <Button type="submit" disabled={loading || deleting}>
                  Add task
                </Button>
              )}
              {mode === "edit" && !isContentDisabled && (
                <Button type="submit" disabled={loading || deleting}>
                  Update task
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}